import React, { useEffect, useState } from "react";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { useAuth } from "@/auth/AuthContext";
import {
  Target, Zap, TrendingUp, Award, CheckCircle2, Clock,
  BookOpen, FileText, Flame, Trophy, Star
} from "lucide-react";

const DailyTargetsPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [view, setView] = useState("today"); // today, streak, leaderboard
  const [targets, setTargets] = useState(null);
  const [streak, setStreak] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (view === "today") {
      loadTodayTargets();
    } else if (view === "streak") {
      loadStreak();
    } else if (view === "leaderboard") {
      loadLeaderboard();
    }
  }, [view]);

  const loadTodayTargets = async () => {
    setLoading(true);
    try {
      const { data } = await managedAxios.get(`${API_BASE}/daily-targets/today`);
      setTargets(data);
    } catch (error) {
      console.error("Error loading targets:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadStreak = async () => {
    setLoading(true);
    try {
      const { data } = await managedAxios.get(`${API_BASE}/daily-targets/streak`);
      setStreak(data);
    } catch (error) {
      console.error("Error loading streak:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const { data } = await managedAxios.get(`${API_BASE}/daily-targets/leaderboard`);
      setLeaderboard(data);
    } catch (error) {
      console.error("Error loading leaderboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = async (type) => {
    try {
      const { data } = await managedAxios.post(`${API_BASE}/daily-targets/mark-complete`, null, {
        params: { target_id: targets.id, completion_type: type }
      });
      toast({ description: data.message });
      loadTodayTargets();
    } catch (error) {
      toast({ variant: "destructive", description: "Could not mark as complete." });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
      {/* Header with Enhanced XP Display */}
      <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold mb-2 flex items-center gap-3">
              <Target className="w-10 h-10" />
              Daily Targets
            </h1>
            <p className="text-yellow-100 text-lg">Complete daily tasks, build streaks, earn XP</p>
          </div>
          {targets && (
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-4 min-w-[140px]">
              <Badge className="bg-amber-600 px-4 py-2 text-xl mb-2 shadow-lg">
                {targets.badge}
              </Badge>
              <div className="mt-2">
                <p className="text-3xl font-bold">{targets.xp_earned}</p>
                <p className="text-sm text-yellow-100">XP Today</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Progress Bar */}
        {targets && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-yellow-100">Daily Progress</span>
              <span className="text-sm font-semibold text-white">{Math.round((targets.xp_earned / 50) * 100)}%</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-3 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-white to-yellow-200 h-full rounded-full transition-all duration-500"
                style={{ width: `${Math.min((targets.xp_earned / 50) * 100, 100)}%` }}
              />
            </div>
            <p className="text-xs text-yellow-100 mt-1">Complete all tasks to earn 50 XP!</p>
          </div>
        )}
      </div>

      {/* View Tabs */}
      <Card className="border-slate-200 bg-white">
        <CardContent className="p-4">
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={view === "today" ? "default" : "outline"}
              onClick={() => setView("today")}
              className="flex-1 sm:flex-none"
            >
              <Target className="w-4 h-4 mr-2" />
              Today's Targets
            </Button>
            <Button
              variant={view === "streak" ? "default" : "outline"}
              onClick={() => setView("streak")}
              className="flex-1 sm:flex-none"
            >
              <Flame className="w-4 h-4 mr-2" />
              Streak Stats
            </Button>
            <Button
              variant={view === "leaderboard" ? "default" : "outline"}
              onClick={() => setView("leaderboard")}
              className="flex-1 sm:flex-none"
            >
              <Trophy className="w-4 h-4 mr-2" />
              Leaderboard
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Today's Targets View */}
      {!loading && view === "today" && targets && (
        <div className="space-y-6">
          {/* Motivation */}
          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
            <CardContent className="p-6 text-center">
              <Star className="w-12 h-12 text-purple-600 mx-auto mb-3" />
              <p className="text-lg font-semibold text-purple-900">{targets.motivation_line}</p>
            </CardContent>
          </Card>

          {/* Tasks */}
          <div className="space-y-4">
            {/* MCQs */}
            <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-transparent">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-600 p-3 rounded-full">
                      <CheckCircle2 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">Daily MCQs</h3>
                      <p className="text-sm text-slate-600">Solve 10 questions • +10 XP</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleMarkComplete("mcqs")}
                    disabled={targets.completion_details?.mcqs_completed >= 10}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {targets.completion_details?.mcqs_completed >= 10 ? "✓ Done" : "Mark Done"}
                  </Button>
                </div>
                {targets.mcqs && targets.mcqs.length > 0 && (
                  <div className="space-y-4 bg-white rounded-lg border border-blue-200 p-4 max-h-96 overflow-y-auto">
                    {targets.mcqs.map((mcq, idx) => (
                      <div key={idx} className="space-y-2 pb-4 border-b border-slate-200 last:border-b-0">
                        <p className="text-sm font-semibold text-slate-900">{idx + 1}. {mcq.question}</p>
                        <div className="grid grid-cols-1 gap-2">
                          {mcq.options?.map((opt, i) => (
                            <Badge key={i} variant="outline" className="text-xs justify-start">
                              {opt.label}. {opt.text}
                            </Badge>
                          ))}
                        </div>
                        {mcq.correct_answer && (
                          <p className="text-xs text-emerald-700 font-semibold mt-2">Answer: {mcq.correct_answer}</p>
                        )}
                      </div>
                    ))}
                    <p className="text-xs text-blue-700 font-semibold text-center pt-2">Total: {targets.mcqs.length} MCQs</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Current Affairs */}
            <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50 to-transparent">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-emerald-600 p-3 rounded-full">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">Current Affairs</h3>
                      <p className="text-sm text-slate-600">Read 10 key facts • +5 XP</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleMarkComplete("ca")}
                    disabled={targets.completion_details?.ca_reviewed}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {targets.completion_details?.ca_reviewed ? "✓ Done" : "Mark Done"}
                  </Button>
                </div>
                {targets.ca_lines && (
                  <ul className="space-y-2 bg-white rounded-lg border border-emerald-200 p-4">
                    {targets.ca_lines.map((line, idx) => (
                      <li key={idx} className="text-sm text-slate-700 flex items-start gap-2">
                        <span className="text-emerald-600 font-bold">{idx + 1}.</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Revision Points */}
            <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-transparent">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-amber-600 p-3 rounded-full">
                      <BookOpen className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">Revision Points</h3>
                      <p className="text-sm text-slate-600">Review 10 points • +10 XP</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleMarkComplete("revision")}
                    disabled={targets.completion_details?.revision_done}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    {targets.completion_details?.revision_done ? "✓ Done" : "Mark Done"}
                  </Button>
                </div>
                {targets.revision_points && (
                  <ul className="space-y-2 bg-white rounded-lg border border-amber-200 p-4">
                    {targets.revision_points.map((point, idx) => (
                      <li key={idx} className="text-sm text-slate-700 flex items-start gap-2">
                        <span className="text-amber-600 font-bold">•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Mains Question */}
            <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-transparent">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-purple-600 p-3 rounded-full">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">Mains Answer Writing</h3>
                      <p className="text-sm text-slate-600">Write 1 answer • +20 XP</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleMarkComplete("mains")}
                    disabled={targets.completion_details?.mains_attempted}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {targets.completion_details?.mains_attempted ? "✓ Done" : "Mark Done"}
                  </Button>
                </div>
                {targets.mains_question && (
                  <div className="bg-white rounded-lg border border-purple-200 p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-purple-100 text-purple-900 text-xs">
                        {targets.mains_question.gs_paper}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {targets.mains_question.marks} marks
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {targets.mains_question.word_limit} words
                      </Badge>
                    </div>
                    <p className="text-sm font-semibold text-slate-900">{targets.mains_question.question}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Completion Status */}
          {targets.completed && (
            <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
              <CardContent className="p-8 text-center">
                <Trophy className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-emerald-900 mb-2">All Targets Completed! 🎉</h3>
                <p className="text-lg text-emerald-700">You earned {targets.xp_earned} XP today</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Streak Stats View - Enhanced */}
      {!loading && view === "streak" && streak && (
        <div className="space-y-6">
          {/* Main Stats Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card className="border-orange-200 bg-gradient-to-br from-orange-50 to-red-50 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="bg-orange-100 rounded-full p-4 w-20 h-20 mx-auto mb-3 flex items-center justify-center">
                  <Flame className="w-12 h-12 text-orange-600" />
                </div>
                <p className="text-5xl font-bold text-orange-900 mb-1">{streak.current_streak}</p>
                <p className="text-sm text-orange-700 font-semibold">Current Streak</p>
                <p className="text-xs text-orange-600 mt-1">days in a row 🔥</p>
              </CardContent>
            </Card>

            <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="bg-amber-100 rounded-full p-4 w-20 h-20 mx-auto mb-3 flex items-center justify-center">
                  <Trophy className="w-12 h-12 text-amber-600" />
                </div>
                <p className="text-5xl font-bold text-amber-900 mb-1">{streak.longest_streak}</p>
                <p className="text-sm text-amber-700 font-semibold">Longest Streak</p>
                <p className="text-xs text-amber-600 mt-1">personal best 🏆</p>
              </CardContent>
            </Card>

            <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="bg-blue-100 rounded-full p-4 w-20 h-20 mx-auto mb-3 flex items-center justify-center">
                  <CheckCircle2 className="w-12 h-12 text-blue-600" />
                </div>
                <p className="text-5xl font-bold text-blue-900 mb-1">{streak.total_days_active}</p>
                <p className="text-sm text-blue-700 font-semibold">Total Days Active</p>
                <p className="text-xs text-blue-600 mt-1">lifetime progress ✨</p>
              </CardContent>
            </Card>

            <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="bg-emerald-100 rounded-full p-4 w-20 h-20 mx-auto mb-3 flex items-center justify-center">
                  <Clock className="w-12 h-12 text-emerald-600" />
                </div>
                <p className="text-lg font-bold text-emerald-900 mb-1">
                  {streak.last_activity ? new Date(streak.last_activity).toLocaleDateString() : 'Not yet'}
                </p>
                <p className="text-sm text-emerald-700 font-semibold">Last Active</p>
                <p className="text-xs text-emerald-600 mt-1">keep it going! 💪</p>
              </CardContent>
            </Card>
          </div>

          {/* Streak Motivation Card */}
          <Card className="border-purple-200 bg-gradient-to-r from-purple-50 via-pink-50 to-orange-50">
            <CardContent className="p-6 text-center">
              <Star className="w-16 h-16 text-purple-600 mx-auto mb-3" />
              <h3 className="text-2xl font-bold text-purple-900 mb-2">
                {streak.current_streak === 0 
                  ? "Start Your Streak Today!" 
                  : streak.current_streak >= 7 
                  ? "Amazing! You're on Fire! 🔥" 
                  : "Keep Going! Build Your Streak! 💪"}
              </h3>
              <p className="text-slate-700">
                {streak.current_streak === 0 
                  ? "Complete your daily targets to start building your streak" 
                  : streak.current_streak >= 7 
                  ? `${streak.current_streak} days of consistent practice! You're unstoppable!` 
                  : `Just ${7 - streak.current_streak} more days to reach your 7-day milestone!`}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Leaderboard View */}
      {!loading && view === "leaderboard" && leaderboard && (
        <div className="space-y-4">
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <p className="text-sm text-center">
                <strong>Your Rank:</strong> {leaderboard.your_rank || 'Not ranked yet'} / {leaderboard.total_users}
              </p>
            </CardContent>
          </Card>

          {leaderboard.leaderboard?.map((entry, idx) => (
            <Card key={idx} className={`border-slate-200 ${entry._id === user?.id ? 'bg-yellow-50 border-yellow-300' : 'bg-white'}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className={`${idx < 3 ? 'bg-amber-500' : 'bg-slate-500'} text-white w-8 h-8 rounded-full flex items-center justify-center`}>
                      {idx + 1}
                    </Badge>
                    <div>
                      <p className="text-sm font-semibold">User {entry._id.slice(0, 8)}</p>
                      <p className="text-xs text-slate-600">{entry.days_active} days active</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-slate-900">{entry.total_xp}</p>
                    <p className="text-xs text-slate-600">XP</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DailyTargetsPage;
